import { useState, useEffect, useCallback } from 'react'
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession } from 'amazon-cognito-identity-js'

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || ''
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || ''

const userPool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId,
})

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: CognitoUser | null
  email: string | null
  token: string | null
}

interface UseAuthReturn extends AuthState {
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
  getToken: () => Promise<string | null>
}

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    email: null,
    token: null,
  })

  // Check for existing session on mount
  useEffect(() => {
    const currentUser = userPool.getCurrentUser()

    if (currentUser) {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            email: null,
            token: null,
          })
          return
        }

        const idToken = session.getIdToken()
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: currentUser,
          email: idToken.payload.email || null,
          token: idToken.getJwtToken(),
        })
      })
    } else {
      setState((prev) => ({ ...prev, isLoading: false }))
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      })

      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      })

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const idToken = session.getIdToken()
          setState({
            isAuthenticated: true,
            isLoading: false,
            user: cognitoUser,
            email: idToken.payload.email || null,
            token: idToken.getJwtToken(),
          })
          resolve()
        },
        onFailure: (err) => {
          reject(new Error(err.message || 'Authentication failed'))
        },
        newPasswordRequired: () => {
          reject(new Error('Password change required. Please contact an administrator.'))
        },
      })
    })
  }, [])

  const signOut = useCallback(() => {
    const currentUser = userPool.getCurrentUser()
    if (currentUser) {
      currentUser.signOut()
    }
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      email: null,
      token: null,
    })
  }, [])

  const getToken = useCallback(async (): Promise<string | null> => {
    const currentUser = userPool.getCurrentUser()

    if (!currentUser) return null

    return new Promise((resolve) => {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null)
          return
        }
        resolve(session.getIdToken().getJwtToken())
      })
    })
  }, [])

  return {
    ...state,
    signIn,
    signOut,
    getToken,
  }
}
